import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Check, Sparkles, ArrowRight, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

const FREE_FEATURES = [
  "Hasta 2 workspaces",
  "Hasta 2 tableros propios",
  "10 MB de almacenamiento total",
  "Las 3 vistas: Rolls, Sprints y Matriz",
  "Sin tarjeta de crédito",
];

const PRO_FEATURES = [
  "Workspaces y tableros ilimitados",
  "50 MB de almacenamiento por usuario",
  "Drag & drop, etiquetas, checklists",
  "Comentarios con Markdown",
  "Modo claro y oscuro",
  "Soporte por email",
];

const CORPORATE_FEATURES = [
  "Todo lo del plan Pro",
  "50 MB por usuario",
  "Mínimo 3 usuarios, sin tope superior",
  "Panel de admin de organización",
  "Estadísticas de uso y accesos",
  "Chat interno del equipo (DMs + broadcast)",
  "Soporte prioritario",
];

function fmtPrice(amount, currency) {
  const locale = currency === "usd" ? "en-US" : "es-MX";
  const opts = { style: "currency", currency: currency.toUpperCase(), minimumFractionDigits: 0, maximumFractionDigits: 2 };
  try { return new Intl.NumberFormat(locale, opts).format(amount); }
  catch { return `${amount} ${currency.toUpperCase()}`; }
}

export default function PricingSection({ onCTAClick }) {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("monthly");    // monthly | annual
  const [currency, setCurrency] = useState("usd");  // usd | mxn

  const { data: plans } = useQuery({
    queryKey: ["public-plans"],
    queryFn: async () => (await api.get("/billing/plans")).data,
    staleTime: 5 * 60 * 1000,
  });

  const { pro, corporate } = useMemo(() => {
    if (!plans) return { pro: null, corporate: null };
    return {
      pro: plans[`individual_${cycle}_${currency}`],
      corporate: plans[`corporate_${cycle}_${currency}`],
    };
  }, [plans, cycle, currency]);

  const monthlyEq = (cfg) => cfg.cycle === "monthly" ? cfg.amount : (cfg.amount / 12);
  const annualSavings = (cycle === "annual" && plans)
    ? (() => {
        const m = plans[`individual_monthly_${currency}`]?.amount || 0;
        const a = plans[`individual_annual_${currency}`]?.amount || 0;
        const yearly = m * 12;
        if (yearly === 0 || a === 0) return 0;
        return Math.round(((yearly - a) / yearly) * 100);
      })()
    : 0;

  return (
    <section id="pricing" className="relative py-20 md:py-28" data-testid="landing-pricing">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400">
            Precios
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-2 leading-[1.05]">
            Empieza gratis. Crece sin sorpresas.
          </h2>
          <p className="text-muted-foreground mt-4">
            Tres planes claros: <strong>Gratis</strong>, <strong>Pro</strong> y <strong>Corporativo</strong>. Escala cuando lo necesites, cancela cuando quieras.
          </p>
        </div>

        {/* Toggles */}
        <div className="flex flex-col items-center gap-3 mt-10">
          <div className="glass-panel rounded-full p-0.5 inline-flex" data-testid="pricing-cycle-toggle">
            {[
              { v: "monthly", lbl: "Mensual" },
              { v: "annual", lbl: "Anual" },
            ].map(o => (
              <button
                key={o.v}
                onClick={() => setCycle(o.v)}
                data-testid={`pricing-cycle-${o.v}`}
                aria-pressed={cycle === o.v}
                className={cn(
                  "px-5 h-10 rounded-full text-sm font-medium transition inline-flex items-center gap-2",
                  cycle === o.v ? "bg-blue-600 text-white shadow-md" : "hover:bg-white/40 dark:hover:bg-white/5"
                )}
              >
                {o.lbl}
                {o.v === "annual" && annualSavings > 0 && (
                  <span className={cn(
                    "text-[10px] font-bold px-1.5 py-0.5 rounded-full",
                    cycle === "annual" ? "bg-white/25 text-white" : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                  )}>
                    ahorra {annualSavings}%
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="inline-flex gap-1 items-center text-xs" data-testid="pricing-currency-toggle">
            <span className="text-muted-foreground">Moneda:</span>
            {["usd", "mxn"].map(c => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                data-testid={`pricing-currency-${c}`}
                aria-pressed={currency === c}
                className={cn(
                  "px-2.5 h-7 rounded-full font-semibold uppercase transition",
                  currency === c ? "bg-blue-500/15 text-blue-700 dark:text-blue-300"
                                 : "text-muted-foreground hover:bg-white/40 dark:hover:bg-white/5"
                )}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          <PlanCard
            testId="plan-free"
            kind="Gratis"
            tagline="Para probar Sprintroll sin compromiso"
            priceOverride={fmtPrice(0, currency)}
            cycleLabel={cycle === "annual" ? "año" : "mes"}
            features={FREE_FEATURES}
            ctaLabel="Empezar gratis"
            onCTAClick={onCTAClick}
          />
          <PlanCard
            testId="plan-pro"
            kind="Pro"
            tagline="Para freelancers y proyectos personales"
            cfg={pro}
            cycle={cycle}
            monthlyEq={pro ? monthlyEq(pro) : 0}
            features={PRO_FEATURES}
            ctaLabel="Elegir Pro"
            highlight
            onCTAClick={onCTAClick}
          />
          <PlanCard
            testId="plan-corporate"
            kind="Corporativo"
            tagline="Para equipos a partir de 3 personas"
            cfg={corporate}
            cycle={cycle}
            monthlyEq={corporate ? monthlyEq(corporate) : 0}
            features={CORPORATE_FEATURES}
            ctaLabel="Ver detalles"
            onCTAClick={() => navigate("/for-teams")}
          />
        </div>

        <p className="text-xs text-center text-muted-foreground mt-8">
          Pagos con PayPal · Cancela cuando quieras · Sin permanencia
        </p>
      </div>
    </section>
  );
}

function PlanCard({ testId, kind, tagline, cfg, cycle, monthlyEq, features, ctaLabel, highlight, onCTAClick, priceOverride, cycleLabel }) {
  const isFree = !!priceOverride;
  if (!isFree && !cfg) {
    return (
      <div className={cn("glass-card rounded-3xl p-8 animate-pulse", highlight && "ring-2 ring-blue-500/30")}>
        <div className="h-5 w-32 bg-muted/30 rounded mb-4" />
        <div className="h-10 w-40 bg-muted/30 rounded mb-2" />
      </div>
    );
  }
  const perSeatNote = !isFree && cfg.per_seat ? ` / usuario` : "";
  return (
    <div
      data-testid={testId}
      className={cn(
        "relative glass-card rounded-3xl p-8 transition will-change-transform flex flex-col",
        highlight
          ? "ring-2 ring-blue-500/40 shadow-xl shadow-blue-500/15 -translate-y-1"
          : "hover:-translate-y-1 hover:shadow-lg"
      )}
    >
      {highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-600 text-white text-[10px] font-bold uppercase tracking-wider">
          <Sparkles className="h-3 w-3" /> Recomendado
        </span>
      )}
      <h3 className="font-display text-2xl font-semibold flex items-center gap-2">
        {kind}
        {isFree && <Zap className="h-4 w-4 text-blue-500" />}
      </h3>
      <p className="text-sm text-muted-foreground mt-1">{tagline}</p>

      <div className="mt-6 flex items-baseline gap-1.5">
        <span className="font-display text-5xl font-semibold">
          {isFree ? priceOverride : fmtPrice(cfg.amount, cfg.currency)}
        </span>
        <span className="text-sm text-muted-foreground">
          /{isFree ? cycleLabel : (cycle === "annual" ? "año" : "mes")}{perSeatNote}
        </span>
      </div>
      {!isFree && cycle === "annual" && (
        <p className="text-xs text-muted-foreground mt-1">
          equivale a {fmtPrice(monthlyEq, cfg.currency)}/mes
        </p>
      )}
      {!isFree && cfg.per_seat && (
        <p className="text-xs text-blue-600 dark:text-blue-300 mt-1 font-medium">
          Mínimo {cfg.min_seats || 3} usuarios
        </p>
      )}
      {isFree && (
        <p className="text-xs text-emerald-600 dark:text-emerald-300 mt-1 font-medium">
          Para siempre · Sin tarjeta
        </p>
      )}

      <ul className="space-y-2.5 mt-7 flex-1">
        {features.map(f => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className="h-4 w-4 mt-0.5 text-emerald-500 flex-none" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        data-testid={`${testId}-cta`}
        onClick={onCTAClick}
        className={cn(
          "w-full mt-8 h-12 rounded-full font-medium transition inline-flex items-center justify-center gap-2",
          highlight
            ? "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/30"
            : "border border-blue-500/30 hover:bg-blue-500/5"
        )}
      >
        {ctaLabel} <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
